import { useEffect } from 'react';

const DIALOG_SELECTOR = [
  '.modal.open .modal-card',
  '.modal-backdrop .deal-modal',
  '.checkout-modal-overlay .checkout-modal',
  '.chat-details-shell.open',
].join(',');

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
}

export function useDialogAccessibility() {
  useEffect(() => {
    let activeDialog: HTMLElement | null = null;
    let returnFocusTo: HTMLElement | null = null;
    let titleIndex = 0;

    const activate = (dialog: HTMLElement) => {
      activeDialog = dialog;
      returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.tabIndex = -1;

      const title = dialog.querySelector<HTMLElement>('h1,h2,h3');
      if (title) {
        if (!title.id) title.id = `clientix-dialog-title-${++titleIndex}`;
        dialog.setAttribute('aria-labelledby', title.id);
      }

      document.body.classList.add('dialog-open');
      requestAnimationFrame(() => {
        const focusable = getFocusable(dialog);
        (focusable[0] || dialog).focus();
      });
    };

    const deactivate = () => {
      if (!activeDialog) return;
      activeDialog = null;
      document.body.classList.remove('dialog-open');
      const target = returnFocusTo;
      returnFocusTo = null;
      requestAnimationFrame(() => target?.isConnected && target.focus());
    };

    const syncDialog = () => {
      const nextDialog = document.querySelector<HTMLElement>(DIALOG_SELECTOR);
      if (nextDialog === activeDialog) return;
      deactivate();
      if (nextDialog) activate(nextDialog);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!activeDialog) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        const closeButton = activeDialog.querySelector<HTMLElement>(
          '.modal-close,.deal-modal-close,.modal-close-btn,.modal-head .icon-btn'
        );
        closeButton?.click();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable(activeDialog);
      if (focusable.length === 0) {
        event.preventDefault();
        activeDialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const observer = new MutationObserver(syncDialog);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    document.addEventListener('keydown', handleKeyDown, true);
    syncDialog();

    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      deactivate();
    };
  }, []);
}
